import { BulleAide, ButtonExternalLink, FieldInput, FieldPatternInput, FieldRadioGroup, Question } from '@workspace/core-ts/components';
import { cn, SIGNATURE_METHOD_OPTIONS } from '@workspace/utils';
import { useEffect, useMemo } from 'react';
import { createSearchParams } from 'react-router-dom';
import { signatureStepContent } from '../../content';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '../../contexts';
import { useContent, useSource } from '../../hooks';
import { YousignLogo } from '../../icons';
import { OnlineActivation } from '../../icons-amo';
import type { StepContentConfig } from '../../utils';
import { variables } from '../../utils';

interface SignatureStepProps {
    content?: StepContentConfig;
    showHeaderIcon?: boolean;
    headerIcon?: React.ComponentType<any>;
}

export function SignatureStep({ content = signatureStepContent, showHeaderIcon = true, headerIcon: HeaderIcon = OnlineActivation }: SignatureStepProps) {
    const methods = useSmartFormGlobal<any>();
    const { setDisableNavigation, others, actions, origin } = useGlobalForm();
    const r = useContent();
    const { isConseiller, isCourtier } = useSource();

    const isIndirect = isConseiller || isCourtier;
    const isMae = origin === 'mae';

    const method = methods.watch('DemandeContrat.SignatureMethod') || 'sms';
    const email = methods.watch('DemandeTarif.ListePersonnes.0.Email');
    const phone = methods.watch('DemandeTarif.ListePersonnes.0.TelPortable');
    const signatureEmail = methods.watch('DemandeContrat.SignatureEmail');
    const signaturePhone = methods.watch('DemandeContrat.SignaturePhone');

    const lastSignature = others?.signatures?.[others?.signatures?.length - 1];

    useEffect(() => {
        if (isIndirect) return;

        setDisableNavigation(true);

        if (!signatureEmail && email) methods.setValue('DemandeContrat.SignatureEmail', email);
        if (!signaturePhone && phone) methods.setValue('DemandeContrat.SignaturePhone', phone);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIndirect, setDisableNavigation, email, phone, signatureEmail, signaturePhone]);

    useEffect(() => {
        if (isIndirect) return;

        if (lastSignature?.signed || (!lastSignature?.url && lastSignature?.signed !== false)) {
            actions.goNextQuestion();
        }
    }, [isIndirect, lastSignature?.signed, lastSignature?.url, actions]);

    const signatureUrl = useMemo(() => {
        if (!lastSignature?.url) return '#';

        const searchParams = createSearchParams({
            method,
            customer: method === 'sms' ? 'phone' : 'email',
            url_return: variables.APP_URL,
        });

        return `${lastSignature.url}?${searchParams.toString()}`;
    }, [lastSignature?.url, method]);

    if (isIndirect) {
        return (
            <div className="mx-auto max-w-3xl padding-root text-center py-12">
                {showHeaderIcon && HeaderIcon && <HeaderIcon className="mx-auto mb-6 w-24 h-24" />}
                <Question.Title className="mb-4">Signature électronique</Question.Title>
                <p className="text-lg text-gray-600">
                    {isMae
                        ? "Un lien de signature a été envoyé à l'adhérent"
                        : "Un lien de signature a été envoyé au client"}
                </p>
            </div>
        );
    }

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto max-w-3xl padding-root"
        >
            <Question.Header>
                {showHeaderIcon && HeaderIcon && <HeaderIcon />}
                <Question.Title>{r(content.mainTitle)}</Question.Title>
                <Question.SubTitle className="text-secondary sm:text-xs text-xs flex items-center gap-2">
                    Avec notre partenaire <YousignLogo className="h-6 w-auto inline" />
                </Question.SubTitle>
            </Question.Header>

            <div className="space-y-6">
                <FieldRadioGroup
                    label={r(content.signatureMethodLabel)}
                    name="DemandeContrat.SignatureMethod"
                    options={SIGNATURE_METHOD_OPTIONS}
                    type="small"
                />

                <FieldPatternInput
                    label={r(content.phoneLabel)}
                    name="DemandeContrat.SignaturePhone"
                    type="phone"
                    description={r(content.phoneDescription)}
                    className={cn(method === 'sms' ? 'block' : 'hidden')}
                    disabled={true}
                />

                <FieldInput
                    label={r(content.emailLabel)}
                    name="DemandeContrat.SignatureEmail"
                    type="email"
                    description={r(content.emailDescription)}
                    className={cn(method === 'email' ? 'block' : 'hidden')}
                    disabled={true}
                />

                <ButtonExternalLink
                    href={signatureUrl}
                    className="w-full"
                >
                    {r(content.buttonText)}
                </ButtonExternalLink>

                <BulleAide title={r(content.helpTitle)}>{r(content.helpContent)}</BulleAide>
            </div>
        </SmartFormGlobal>
    );
}
